import { useTranslation } from 'react-i18next';

import { isAdmin } from '@/functions/auth';
import { FilterProps } from '@/models/FilterConfig';
import {
  KlassTypeEnum,
  KlassStatusEnum,
  DaysOfWeekTypeEnum,
} from '@/models/Klass';
import User, { UserStatusEnum, UserTypeEnum } from '@/models/User';
import {
  createMultipleTeacherFilter,
  createMultipleUnitFilter,
} from '@/utils/filterFactory';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import { courseBasesQueryKeys } from '../services/querykeys';
import { useFilterLabelTranslated } from '@/components/filter/Filter.hooks';
import { CourseFormatEnum } from '@/models/Course';
import { canAccessMultipleUnits } from '@/utils/canAccessMultipleUnits';
import {
  ListKlassesFilters,
  orderingKlassDate,
} from '../services/klassServices';
import { Fields } from '@/utils/searchParamsToObject';

export type OmitParamsListKlassesFilters = Omit<
  ListKlassesFilters,
  'ordering'
> & {
  klassStartDateOrdering: orderingKlassDate;
  klassEndDateOrdering: orderingKlassDate;
  klassStatus?: KlassStatusEnum[];
  klassId?: Array<number>;
};

type UseKlassFiltersProps = {
  filter: Partial<OmitParamsListKlassesFilters>;
  user: User;
};

export function useKlassFilters(args: UseKlassFiltersProps) {
  const { user, filter = {} } = args;

  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { translate } = useFilterLabelTranslated();

  const isHighStaff = isAdmin(user.userType);

  const periodValue: Partial<OmitParamsListKlassesFilters> = {
    timeAfter: filter.timeAfter,
    timeBefore: filter.timeBefore,
  };

  const configs: FilterProps[] = [
    {
      type: 'search',
      label: t('klass'),
      debounce: true,
      name: 'name',
      placeholder: t('research'),
    },
    createMultipleUnitFilter({
      hide: !canAccessMultipleUnits(user),
      filters: { idIn: getAuthorizedUnits(user)?.toString() },
    }),
    createMultipleTeacherFilter({
      hide: !isHighStaff,
      filters: {
        unitId: getAuthorizedUnits(user),
        userType: [UserTypeEnum.TEACHER],
        status: [UserStatusEnum.REGISTRED],
      },
    }),
    {
      name: 'courseFormats',
      label: t('modality'),
      type: 'multiple',
      mode: 'static',
      options: Object.values(CourseFormatEnum).map(format => ({
        label: translate('courseFormats', format),
        value: format,
      })),
    },
    {
      name: 'klassStatus',
      label: t('status'),
      type: 'multiple',
      mode: 'static',
      options: Object.values(KlassStatusEnum).map(klassStatus => ({
        label: translate('klassStatus', klassStatus),
        value: klassStatus,
      })),
    },
    {
      type: 'multiple',
      name: 'klassType',
      label: t('klassType'),
      mode: 'static',
      options: Object.values(KlassTypeEnum).map(klassType => ({
        label: translate('klassType', klassType),
        value: klassType,
      })),
    },
    {
      name: 'courseId',
      label: t('course'),
      type: 'multiple',
      mode: 'dynamic',
      service: courseBasesQueryKeys.list,
      makeOptions: options =>
        options.map(option => ({
          label: option.abbreviation,
          value: option.id,
        })),
    },
    {
      name: 'dayOfWeek',
      label: t('day'),
      type: 'multiple',
      mode: 'static',
      options: Object.values(DaysOfWeekTypeEnum)
        .filter(dayOfWeek => dayOfWeek !== DaysOfWeekTypeEnum.SUNDAY)
        .map(dayOfWeek => ({
          label: translate('dayOfWeek', dayOfWeek),
          value: dayOfWeek,
        })),
    },
    {
      label: t('otherFilters'),
      type: 'multipleBoolean',
      options: [
        {
          name: 'startSoon',
          label: t('startSoon'),
          type: 'boolean',
        },
        {
          name: 'endSoon',
          label: t('endSoon'),
          type: 'boolean',
        },
        {
          name: 'hasActiveLessons',
          label: t('hasActiveLessons'),
          type: 'boolean',
        },
      ],
    },
    {
      label: t('periodOfTheDay'),
      type: 'selectComposite',
      value: periodValue,
      options: [
        {
          label: t('morning'),
          value: { timeAfter: '00:00:00', timeBefore: '11:59:00' },
        },
        {
          label: t('afternoon'),
          value: { timeAfter: '12:00:00', timeBefore: '17:59:59' },
        },
        {
          label: t('evening'),
          value: { timeAfter: '18:00:00', timeBefore: undefined },
        },
      ],
    },
    {
      type: 'dualArrowOrdering',
      label: t('first'),
      name: 'klassStartDateOrdering',
      options: [
        {
          label: t('ascending'),
          value: 'klass_start_date',
        },
        {
          label: t('descending'),
          value: '-klass_start_date',
        },
      ],
    },
    {
      type: 'dualArrowOrdering',
      label: t('termination'),
      name: 'klassEndDateOrdering',
      options: [
        {
          label: t('ascending'),
          value: 'klass_end_date',
        },
        {
          label: t('descending'),
          value: '-klass_end_date',
        },
      ],
    },
  ];

  return { configs };
}

export const klassConfigFields: Fields<OmitParamsListKlassesFilters> = {
  courseLineId: { isArray: true, type: 'number' },
  teacherId: { isArray: true, type: 'number' },
  courseFormats: { isArray: true, type: 'string' },
  dayOfWeek: { isArray: true, type: 'string' },
  startSoon: { type: 'string' },
  endSoon: { type: 'string' },
  hasActiveLessons: { type: 'string' },
  unitId: { isArray: true, type: 'number' },
  courseId: { isArray: true, type: 'number' },
  klassType: { isArray: true, type: 'string' },
  klassStatus: { isArray: true, type: 'string' },
  klassStartDateOrdering: { type: 'string' },
  klassEndDateOrdering: { type: 'string' },
  klassId: { isArray: true, type: 'number' },
};
